// 对话管理相关功能
import { atom } from 'jotai';
import { fetchApi } from '../../../lib/apiClient';
import { 
  ConversationsResponse, 
  ConversationDetail 
} from './types';
import { 
  conversationsAtom, 
  selectedConversationIdAtom, 
  messagesAtom, 
  conversationsLoadingAtom, 
  conversationLoadingAtom, 
  hasMoreMessagesAtom, 
  oldestMessageIdAtom 
} from './baseAtoms';

/**
 * 获取对话列表
 */
export const fetchConversationsAtom = atom(
  null,
  async (get, set, params?: { limit?: number; offset?: number }) => {
    const limit = params?.limit || 8;
    const offset = params?.offset || 0;

    try {
      set(conversationsLoadingAtom, true);

      // 构建URL查询参数 - Path only
      const path = `/chat/conversations?limit=${limit}&offset=${offset}`;

      const response = await fetchApi<ConversationsResponse>(path, {
        method: 'GET'
      });

      set(conversationsAtom, response.conversations);

      // 获取当前选中的对话ID
      const selectedId = get(selectedConversationIdAtom);

      // 检查对话列表是否有内容以及当前选中的对话是否存在于列表中
      if (response.conversations.length > 0) {
        const selectedConversationExists = selectedId && response.conversations.some(conv => conv.id === selectedId);

        // 如果没有选择对话或选择的对话不存在于列表中，则选择最新的对话
        if (!selectedId || !selectedConversationExists) {
          // 找到updatedAt最新的对话
          const mostRecent = [...response.conversations].sort((a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          )[0];

          set(selectedConversationIdAtom, mostRecent.id);
          // 加载最新对话的消息
          set(fetchConversationAtom, mostRecent.id);
        } else if (selectedId && selectedConversationExists) {
          // 如果已选择的对话存在于列表中，确保它被加载
          if (get(messagesAtom).length === 0) {
             set(fetchConversationAtom, selectedId);
          }
        }
      } else {
        // 如果没有对话了，清空选择和消息
        set(selectedConversationIdAtom, null);
        set(messagesAtom, []);
      }

      return response;
    } catch (error) {
      console.error('Failed to load conversations:', error);
      throw error;
    } finally {
      set(conversationsLoadingAtom, false);
    }
  }
);

/**
 * 获取单个对话详情及其最近消息
 */
export const fetchConversationAtom = atom(
  null,
  async (get, set, conversationId: string) => {
    if (!conversationId) return null;

    try {
      // 在开始加载前先设置loading状态
      set(conversationLoadingAtom, true);
      // 在请求开始前记录当前请求的conversationId
      const requestingId = conversationId;

      // 清空消息列表
      set(messagesAtom, []);
      set(hasMoreMessagesAtom, false);
      set(oldestMessageIdAtom, null);

      // 构建URL查询参数 - Path only
      const path = `/chat/conversation?conversationId=${conversationId}`;

      const response = await fetchApi<ConversationDetail>(path, {
        method: 'GET'
      });

      // 检查当前选中的对话ID是否仍然是我们请求的对话ID
      // 只有在当前选中的ID与请求时的ID相同时才更新消息
      if (get(selectedConversationIdAtom) === requestingId) {
          set(messagesAtom, response.recentMessages);
          set(hasMoreMessagesAtom, response.hasMoreMessages);

          // 设置最早消息的ID，用于加载更多
          if (response.recentMessages.length > 0) {
            set(oldestMessageIdAtom, response.recentMessages[0].id);
          }
      }

      return response;
    } catch (error) {
      console.error('Failed to load conversation:', error);
      throw error;
    } finally {
      // 检查当前选中的会话ID是否是我们请求的ID
      // 只有当前选中的ID与请求的ID相同时才设置loading为false
      // 这样可以避免将新的请求的loading状态意外地设置为false
      if (get(selectedConversationIdAtom) === conversationId) {
        set(conversationLoadingAtom, false);
      } else {
        // 如果当前选中的ID不是我们请求的ID，检查是否有其他请求正在进行
        // 如果没有其他请求在进行，可以安全地设置loading为false
        const currentSelectedId = get(selectedConversationIdAtom);
        if (!currentSelectedId) {
          set(conversationLoadingAtom, false);
        }
      }
    }
  }
);

/**
 * 删除对话
 */
export const deleteConversationAtom = atom(
  null,
  async (get, set, conversationId: string) => {
    if (!conversationId) return;

    try {
      const data = await fetchApi('/chat/conversation/delete', {
        method: 'POST',
        body: {
          conversationId
        }
      });

      const currentConversations = get(conversationsAtom);
      const updatedConversations = currentConversations.filter(c => c.id !== conversationId);
      set(conversationsAtom, updatedConversations);

      // 如果删除的是当前选中的对话，选择最新的一个对话或清空
      const selectedId = get(selectedConversationIdAtom);
      if (selectedId === conversationId) {
        if (updatedConversations.length > 0) {
           // 选择最新的对话
           const mostRecent = [...updatedConversations].sort((a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          )[0];
          set(selectedConversationIdAtom, mostRecent.id);
          set(fetchConversationAtom, mostRecent.id); // 加载新选中的对话
        } else {
          // 没有对话了
          set(selectedConversationIdAtom, null);
          set(messagesAtom, []);
          set(conversationLoadingAtom, false);
          set(hasMoreMessagesAtom, false);
          set(oldestMessageIdAtom, null);
        }
      }

      return data;
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      throw error;
    }
  }
);

/**
 * 创建新对话
 */
export const createNewConversationAtom = atom(
  null,
  (get, set) => {
    if (get(conversationLoadingAtom) || get(conversationsLoadingAtom)) {
        console.warn("Attempted to create new conversation while loading.");
        return;
    }
    // 设置为特殊值 "new" 表示一个临时的、未保存的新对话状态
    set(selectedConversationIdAtom, "new");
    set(messagesAtom, []);
    set(conversationLoadingAtom, false); // 没有实际加载
    set(hasMoreMessagesAtom, false);
    set(oldestMessageIdAtom, null);
  }
);
